/**
 * Project Numbering Settings
 * --------------------------
 * Lets the operator inspect and seed the global project-number counter.
 *
 * The counter lives in `counters/projectNumbers/state/global` (managed by the
 * `getOrSetProjectNumberCounter` callable). We never write to it directly
 * from the client — every write goes through the callable so the
 * monotonic-only invariant is enforced server-side.
 */

import React, { useEffect, useState } from 'react';
import { Hash, Loader2, ArrowUpFromLine, AlertTriangle, Check } from 'lucide-react';
import { httpsCallable, getFunctions } from 'firebase/functions';

interface CounterState {
  next: number;
  startBaseline: number;
}

const ProjectNumberingSettings: React.FC = () => {
  const [counter, setCounter] = useState<CounterState | null>(null);
  const [loading, setLoading] = useState(true);
  const [baselineDraft, setBaselineDraft] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedOnce, setSavedOnce] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable(getFunctions(), 'getOrSetProjectNumberCounter');
        const { data } = await fn({});
        if (cancelled) return;
        const c = data as CounterState;
        setCounter(c);
        // Seed the input with the next-available number so users can type
        // a "starting from" value relative to where the counter actually is.
        setBaselineDraft(String(c.next || 1));
      } catch (e) {
        console.error('[ProjectNumberingSettings] load failed', e);
        if (!cancelled) setCounter({ next: 0, startBaseline: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const formatPreview = (n: number): string => `#${String(Math.max(0, Math.floor(n))).padStart(4, '0')}`;

  const handleSaveBaseline = async (opts: { force?: boolean } = {}) => {
    setSaveError(null);
    const proposed = Math.floor(Number(baselineDraft));
    if (!Number.isFinite(proposed) || proposed < 1) {
      setSaveError('Enter a whole number 1 or greater.');
      return;
    }
    const isLowering = !!counter && counter.next > 0 && proposed < counter.next;
    if (isLowering && !opts.force) {
      // Surface the dedicated "Force-reset" path below; we don't auto-force
      // from this primary action button so users can't accidentally rewind.
      setSaveError(
        `Counter is at ${formatPreview(counter!.next)}. Lowering to ${formatPreview(proposed)} requires the Force-reset button below.`,
      );
      return;
    }
    if (counter && counter.next > 0 && proposed === counter.next) {
      // No-op: already where they want it.
      setSavedOnce(true);
      setTimeout(() => setSavedOnce(false), 1500);
      return;
    }
    // Ask once before changing — this is a one-way operation.
    const verb = (counter?.next ?? 0) > 0 ? 'change' : 'set';
    const baseConfirm = `${verb === 'set' ? 'Seed' : 'Move'} the project counter so the next number reserved is ${formatPreview(proposed)}?\n\nThis only affects NEW projects. Existing project numbers (including legacy YY-NNN values like 26-005) are not changed.`;
    const forceConfirm = `FORCE-RESET the project counter from ${formatPreview(counter?.next || 0)} down to ${formatPreview(proposed)}?\n\nNumbers ${formatPreview(proposed)}-${formatPreview((counter?.next || 0) - 1)} will be skipped (the counter only advances, never reuses). This is safe ONLY if no #NNNN-format projects already exist in that range — typical for an initial bulk migration of legacy YY-NNN projects.`;
    if (!window.confirm(opts.force ? forceConfirm : baseConfirm)) return;

    setSaving(true);
    try {
      const fn = httpsCallable(getFunctions(), 'getOrSetProjectNumberCounter');
      const { data } = await fn({ setBaseline: proposed, force: !!opts.force });
      const c = data as CounterState;
      setCounter(c);
      setBaselineDraft(String(c.next));
      setSavedOnce(true);
      setTimeout(() => setSavedOnce(false), 2000);
    } catch (e: any) {
      console.error('[ProjectNumberingSettings] save failed', e);
      setSaveError(e?.message || 'Failed to update counter.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 24, color: 'var(--color-text-secondary)' }}>
        <Loader2 size={16} className="spin" /> Loading counter...
      </div>
    );
  }

  const next = counter?.next ?? 0;
  const baseline = counter?.startBaseline ?? 0;
  const previewNext = formatPreview(Number(baselineDraft) || 0);
  const proposedRaw = Math.floor(Number(baselineDraft));
  const wantsLower = next > 0 && Number.isFinite(proposedRaw) && proposedRaw >= 1 && proposedRaw < next;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 640 }}>
      <div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Hash size={18} /> Project Numbering
        </h2>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.5 }}>
          New projects are assigned a monotonic number from a single global counter
          (e.g. <code>#0048</code>). The counter only goes up — numbers are never
          reused, even if a deal is deleted. Existing legacy numbers like
          <code style={{ marginLeft: 4 }}>26-005</code> are left alone.
        </p>
      </div>

      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
          Current state
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>Next number to assign</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--pm-accent)', fontVariantNumeric: 'tabular-nums' }}>
              {next > 0 ? formatPreview(next) : '\u2014'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>Started at</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
              {baseline > 0 ? formatPreview(baseline) : '\u2014'}
            </div>
          </div>
        </div>
        {next === 0 && (
          <p style={{ marginTop: 10, fontSize: 12, color: 'var(--color-warning-text)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            The counter has not been seeded yet. Set a starting number below before assigning numbers in production.
          </p>
        )}
      </div>

      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 4 }}>
          {next > 0 ? 'Move the counter forward' : 'Seed the counter'}
        </div>
        <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: '0 0 12px', lineHeight: 1.45 }}>
          {next > 0
            ? `Pick the number you want the NEXT new project to receive. Must be ${formatPreview(next)} or higher (use Force-reset below to go lower).`
            : 'Pick the number you want the FIRST new project to receive. For example, if you have completed 47 projects historically, set this to 48 so the next assigned number is #0048.'}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
            Next number:
            <input
              type="number"
              min={1}
              step={1}
              value={baselineDraft}
              onChange={e => { setBaselineDraft(e.target.value); setSaveError(null); }}
              disabled={saving}
              style={{ width: 110, padding: '6px 9px', border: '1px solid var(--color-border)', borderRadius: 7, fontSize: 13, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}
            />
          </label>
          <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>preview <code style={{ fontWeight: 600, color: 'var(--pm-accent)' }}>{previewNext}</code></span>
          {!wantsLower && (
            <button
              type="button"
              onClick={() => handleSaveBaseline()}
              disabled={saving || !baselineDraft}
              style={{
                padding: '7px 14px',
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 8,
                border: 'none',
                background: '#2C3E2F',
                color: '#fff',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {saving ? <Loader2 size={13} className="spin" /> : <ArrowUpFromLine size={13} />}
              {saving ? 'Saving...' : next > 0 ? 'Move counter' : 'Seed counter'}
            </button>
          )}
          {savedOnce && (
            <span style={{ fontSize: 12, color: 'var(--color-success-text)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Check size={13} /> Saved
            </span>
          )}
        </div>
        {saveError && (
          <p style={{ marginTop: 10, fontSize: 12, color: 'var(--color-error-text)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} /> {saveError}
          </p>
        )}

        {/* Force-reset path. Only shown when the user has typed a value
            BELOW the current counter — keeps the dangerous button out of
            sight during normal use. The amber styling and explicit
            confirmation step inside `handleSaveBaseline({ force: true })`
            both reinforce that this is a one-way operation. */}
        {wantsLower && (
          <div style={{ marginTop: 14, padding: 12, background: 'rgba(146, 64, 14, 0.08)', border: '1px solid rgba(146, 64, 14, 0.22)', borderRadius: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
              <AlertTriangle size={14} style={{ color: 'var(--color-warning-text)', flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: 12, color: 'var(--color-warning-text)', lineHeight: 1.45 }}>
                You're trying to lower the counter from <strong>{formatPreview(next)}</strong> to <strong>{formatPreview(proposedRaw)}</strong>.
                Numbers in between will be permanently skipped. Only do this if no <code>#NNNN</code>-format projects exist in that range — for example, when seeding the counter to 1 for an initial bulk migration of legacy <code>YY-NNN</code> projects.
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleSaveBaseline({ force: true })}
              disabled={saving}
              style={{
                padding: '6px 12px',
                fontSize: 12.5,
                fontWeight: 600,
                borderRadius: 7,
                border: '1px solid #92400e',
                background: '#92400e',
                color: '#fff',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {saving ? <Loader2 size={13} className="spin" /> : <AlertTriangle size={13} />}
              {saving ? 'Resetting...' : `Force-reset to ${formatPreview(proposedRaw)}`}
            </button>
          </div>
        )}
      </div>

      {/*
        Migration wizard launch lives on the Project Hub banner and on each
        legacy row in the PM Sidebar. Adding a duplicate launcher here would
        spread the entry points without adding value — leave it out.
      */}

      <style>{`@keyframes spinPNS { to { transform: rotate(360deg); } } .spin { animation: spinPNS 0.9s linear infinite; }`}</style>
    </div>
  );
};

export default ProjectNumberingSettings;
